// The editable Cap cell of the Usage & limits table (ENG2-2461 / T8).
//
// Replaces the old CustomNumeric input, which saved 1.5 s after the last
// keystroke with no Save button, so a half-typed value could be written.
// Here nothing is sent until Save / Enter; Cancel / Escape discards.
//
// The editor opens in a popover anchored to the cell, so the cell keeps its
// size and the table columns never shift while editing.
//
// An INT-max cap (2,147,483,647) is shown as that number, like any other cap.

import * as React from 'react'
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import numeral from 'numeral'
import { isValidCounterValue } from '../inputs/validate-numeric'

export const formatCap = (value: number): string => numeral(value).format('0,0')

type Props = {
  label: string
  value: number
  isLoading: boolean
  onSave: (value: number) => void
}

function InlineCapEditor({ label, value, isLoading, onSave }: Props) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)
  const [draft, setDraft] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)
  const editing = Boolean(anchor)

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(String(value))
    setError(null)
    setAnchor(event.currentTarget)
  }

  const close = () => {
    setAnchor(null)
    setError(null)
  }

  const save = () => {
    const parsed = draft.trim() === '' ? NaN : Number(draft)
    if (!isValidCounterValue(parsed)) {
      setError('Enter a whole number, 0 or greater')
      return
    }
    close()
    onSave(Math.floor(parsed))
  }

  const actionLabel = `Edit ${label} cap`

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            color: 'text.primary',
          }}
        >
          {formatCap(value)}
        </Typography>
        <Tooltip title="Edit cap">
          <span>
            <IconButton
              size="small"
              aria-label={actionLabel}
              disabled={isLoading}
              onClick={open}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Popover
        open={editing}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.5, minWidth: 260 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75 }}
          >
            {actionLabel}
          </Typography>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <TextField
              size="small"
              type="number"
              autoFocus
              value={draft}
              inputProps={{
                min: 0,
                step: 1,
                'aria-label': `${label} cap`,
                style: { fontSize: '0.875rem', width: 110 },
              }}
              error={Boolean(error)}
              helperText={error}
              FormHelperTextProps={{
                sx: { mx: 0, mt: 0.5, fontSize: '0.7rem' },
              }}
              onChange={(e) => {
                setDraft(e.target.value)
                if (error) setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  save()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  close()
                }
              }}
            />
            <Button size="small" variant="contained" onClick={save}>
              Save
            </Button>
            <Button size="small" variant="text" onClick={close}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  )
}

export default InlineCapEditor
